/* eslint-disable import/no-duplicates */
import ChevronLeft from '@atom-web/icons-24/jsx/ChevronLeft'
import ChevronRight from '@atom-web/icons-24/jsx/ChevronRight'
import Close from '@atom-web/icons-24/jsx/Close'
import Included from '@atom-web/icons-24/jsx/Included'
import TickMark from '@atom-web/icons-24/TickMark'
import { Dialog } from '@atom-web/macros'
import { Box, Button, Column, Row, Text } from '@atom-web/micros'
import shuffle from 'lodash.shuffle'
import Head from 'next/head'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { Fragment, useCallback } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Element } from 'react-scroll'
import Sticky from 'react-stickynode'

import { AppConfig } from '../../../app-config'
import AmenitiesSection from '../../../components/AmenitiesSection'
import BreadCrumb from '../../../components/BreadCrumb'
import ContentWrapper from '../../../components/ContentWrapper'
import ScrollSpyTabs from '../../../components/ScrollSpyTabs'
import capitializeURL from '../../../utils/capitializeURL'
import getImgIXUrl from '../../../utils/getImgIXUrl'
import { triggerSimpleAjax } from '../../../utils/http-helper'
import getLocale from '../../../utils/locale-helpers/getLocale'
import uniqBy from '../../../utils/lodashUtils/uniqBy'
import getPageDataComponents from '../../../utils/strapi-utils/getPageDataComponents'
import getStrapiMediaData from '../../../utils/strapi-utils/getStrapiMediaData'
import {
	SeoDescWrapper,
	SeoTextSection,
} from '../../destination/components/DescriptionBlockWithReadMore'
import SEOBlock from '../../packages/components/seo-block'
import GCMContainer from '../../packages/gcm/GCMContainer'
import { ImageSlider } from './components/ImageSlider'
import Banner from './components/MaldivesBanner'
import RoomGrid from './components/MaldivesRoomGrid'
import TestimonialCard from './components/TestimonialCard'

const Title = ({ title, style = {} }) => (
	<Text
		as="h2"
		css={{
			fontSize: '$5',
			color: '$black',
			lineHeight: '32px',
			...style,
		}}
	>
		{title}
	</Text>
)

const myLoader = ({ src, width, quality }) => {
	return `${src}?w=${width}&q=${quality || 75}`
}

const routeMapping = {
	room: 'roomDetails',
	'water-sports': 'waterSports',
	highlights: 'propertyHighlights',
}

const MaldivesPrecostedPage = ({
	data,
	isMobile,
	slug,
	localeFromUrl,
	seoData,
}) => {
	const router = useRouter()
	const { query } = router
	const [testimonials, setTestimonials] = useState([])
	const [openRoom, setOpenRoom] = useState(false)
	const [selectedGalleryTab, setSelectedGalleryTab] = useState('')
	const locale = useMemo(() => getLocale({ router, locale: localeFromUrl }))
	const isGlobalUser = useMemo(() => locale && locale !== 'in', [locale])
	const { metaDescription, metaTitle, keywords, canonicalURL, pageContents } =
		useMemo(() => {
			return {
				metaDescription: seoData?.seo?.metaDescription,
				metaTitle: seoData?.seo?.metaTitle,
				keywords: seoData?.seo.keywords,
				canonicalURL: seoData?.seo.canonicalURL,
				pageContents: seoData?.pageContents,
			}
		}, [seoData?.seo])
	useEffect(() => {
		triggerSimpleAjax({
			url: 'https://s3.ap-south-1.amazonaws.com/oceanjar-new/ui/json_static/mle-testimonials-v3.json',
			method: 'GET',
			requesHeaders: {
				'Content-Type': 'application/json',
			},
			onSuccess: data => setTestimonials(data),
		})
	}, [])

	useEffect(() => {
		if (
			query.gallery === 'room' ||
			query.gallery === 'water-sports' ||
			query.gallery === 'highlights'
		) {
			setOpenRoom(true)
			setSelectedGalleryTab(query.gallery)
		}
	}, [query])

	const tabs = useMemo(() => {
		const arr = []
		const objKeys = Object.keys(data)

		if (objKeys?.includes('roomDetails')) {
			arr.push({
				name: 'Rooms Options',
				key: 'room-details',
				offset: isMobile ? -96 : -88,
			})
		}
		if (objKeys?.includes('waterSports')) {
			arr.push({
				name: 'Water Sports',
				key: 'water-sports',
				offset: isMobile ? -86 : -108,
			})
		}
		if (objKeys?.includes('propertyHighlights')) {
			arr.push({
				name: 'Highlights',
				key: 'property-highlights',
				offset: isMobile ? -76 : -108,
			})
		}
		if (objKeys?.includes('amenityDisplayList')) {
			arr.push({
				name: 'Amenities',
				key: 'amenities',
				offset: isMobile ? -64 : -108,
			})
		}
		if (objKeys?.includes('overview')) {
			arr.push({
				name: 'Overview',
				key: 'overview',
				offset: isMobile ? -64 : -108,
			})
		}
		return arr
	}, [isMobile, data])

	const galleryTabs = useMemo(() => {
		const arr = []
		if (data?.resortImages?.length) {
			arr.push({
				text: 'PROPERTY',
				route: 'property',
			})
		}
		if (data?.roomDetails?.length) {
			arr.push({
				text: 'ROOM',
				route: 'room',
			})
		}
		if (data?.waterSports?.length) {
			arr.push({
				text: 'WATERSPORT',
				route: 'water-sports',
			})
		}
		if (data?.propertyHighlights?.length) {
			arr.push({
				text: 'HIGHLIGHTS',
				route: 'highlights',
			})
		}
		return arr
	}, [data])

	const currentGalleryObj = useMemo(
		() =>
			query.gallery && query.gallery !== 'property'
				? [...data?.[routeMapping?.[query?.gallery]]]
				: [],
		[query],
	)

	const showGetCostModal = useCallback(
		({ resortCode, name }) => {
			if (query.m !== isGlobalUser ? 'mle-gcm' : 'gcm') {
				const query = {
					m: isGlobalUser ? 'mle-gcm' : 'gcm',
					resortCode: resortCode,
					rn: 'Maldives',
					rc: 'mle',
					name: name,
					preCosted: 1,
				}
				router.push(
					{
						pathname: router.asPath.split('?')[0],
						query,
					},
					null,
					{ shallow: true, scroll: false },
				)
			}
		},
		[router],
	)
	const bannerMediaImage = useMemo(() => {
		return getStrapiMediaData(seoData?.seo?.metaImage, true)
	}, [seoData?.seo?.metaImage]).url

	const roomOptions = useMemo(() => {
		return shuffle(uniqBy([...(data?.roomDetails || [])], x => x.name)).map(
			roomDetails => {
				return {
					...roomDetails,
					images: shuffle(roomDetails?.images),
				}
			},
		)
	}, [data?.roomDetails])

	return (
		<>
			<Head>
				<title>{metaTitle ? metaTitle : data?.seoDetails?.metaTitle}</title>

				<meta
					name="description"
					content={
						metaDescription
							? metaDescription
							: data?.seoDetails?.metaDescription
					}
				/>

				<meta
					name="keywords"
					content={keywords ? keywords : data?.seoDetails?.metaKeywords}
				/>

				<link
					rel="canonical"
					href={
						canonicalURL
							? `${AppConfig.domain_url}${canonicalURL}`
							: `${AppConfig.domain_url}${
									locale ? `${locale}/` : ''
							  }${router.asPath.split('?')[0]?.replace('/', '')}`
					}
				/>

				<meta property="og:title" content={metaTitle || 'Pickyourtrail'} />
				<meta
					property="og:description"
					content={metaDescription || 'Pickyourtrail'}
				/>
				<meta
					property="og:image"
					content={
						bannerMediaImage ||
						getImgIXUrl(data?.bannerImages[0], 'w=1200&h=600&fit=crop')
					}
				/>
			</Head>
			<Column
				css={{
					paddingBottom: '72px',
				}}
			>
				<Box
					css={{
						position: 'relative',
					}}
				>
					<Banner
						images={data.bannerImages}
						resortName={`${data?.resortName} Maldives Resort`}
						starRating={data?.starRating}
						atoll={data?.atoll}
						gaRating={data?.gaRating}
						googleReviewCount={data?.googleReviewCount}
						tripAdvisorRating={data?.tripAdvisorRating}
					/>
				</Box>
				<Sticky top={0} innerZ={999} flex={1}>
					<Row
						css={{
							width: '100%',
							borderBottom: '1px solid #E4F1EE',
							backgroundColor: '$white',
							height: '70px',
							'@smMax': {
								display: 'none',
							},
						}}
					>
						<ContentWrapper>
							<ScrollSpyTabs tabs={tabs} />
						</ContentWrapper>
					</Row>
					<Row
						css={{
							width: '100%',
							borderBottom: '1px solid #E4F1EE',
							backgroundColor: '$white',
							paddingLeft: '$4',
							flexWrap: 'nowrap',
							overflow: 'auto',
							'@lg': {
								display: 'none',
							},
						}}
					>
						<ScrollSpyTabs tabs={tabs} />
					</Row>
				</Sticky>

				<ContentWrapper>
					<Row css={{ gap: '32px' }}>
						{/* left side content */}
						<Column
							gap="7"
							css={{
								width: '100%',
								'@sm': {
									width: '754px',
								},
								'@lg': {
									marginTop: '$4',
								},
							}}
						>
							{roomOptions && (
								<Element name="room-details">
									<Title
										title={`${data?.resortName} Room Options`}
										style={{
											paddingBottom: '10px',
										}}
									/>
									<RoomGrid
										data={roomOptions}
										isMobile={isMobile}
										loader={myLoader}
										slug={slug}
										route="room"
									/>
								</Element>
							)}
							{data?.waterSports && (
								<Element name="water-sports">
									<Title
										title="Water Sports Activities"
										style={{
											paddingBottom: '10px',
										}}
									/>
									<Row
										css={{
											width: '100%',
											flexWrap: 'wrap',

											'@smMax': {
												flexWrap: 'nowrap',
												overflow: 'scroll',
												paddingBottom: '$5',
												gap: '$2',
											},

											'@lg': {
												gap: '24px 12px',
											},
										}}
									>
										{/* to remove duplicate activities */}
										{(data?.waterSports || [])
											.filter(
												(val1, index, self) =>
													index ===
													self.findIndex(
														val2 => val2.waterSportId === val1.waterSportId,
													),
											)
											.map((value, index) => (
												<Fragment key={'activity-card-' + index}>
													<Column
														gap={2}
														key={'activity-card-' + index}
														css={{
															width: 'calc(33.33% - 8px)',
															'@smMax': {
																display: 'none',
															},
														}}
													>
														<Column
															gap={3}
															css={{
																position: 'relative',
																borderRadius: '3px',
																height: '200px',
															}}
															onClick={() =>
																router.push(
																	`/${slug}?gallery=water-sports&index=${index}`,
																	null,
																	{
																		shallow: true,
																		scroll: false,
																	},
																)
															}
														>
															<Image
																unoptimized
																objectFit="cover"
																src={getImgIXUrl(
																	value?.images[0],
																	'h=240&w=240&fit=crop',
																	80,
																	2,
																)}
																loader={myLoader}
																layout="fill"
																className="room-grid"
															/>
														</Column>
														<Text
															css={{
																fontSize: '$3',
																color: '$grey11',
																fontWeight: '$bold',
																lineHeight: 1.33,
																letterSpacing: '-0.29px',
																'@lg': {
																	fontSize: '$4',
																},
															}}
														>
															{value?.name}
														</Text>
													</Column>
													<Column
														gap={3}
														key={'activity-card-' + index}
														css={{
															position: 'relative',
															borderRadius: '3px',
															'@lg': {
																display: 'none',
															},
														}}
														onClick={() =>
															router.push(
																`/${slug}?gallery=water-sports&index=${index}`,
																null,
																{
																	shallow: true,
																	scroll: false,
																},
															)
														}
													>
														<Image
															unoptimized
															objectFit="cover"
															src={getImgIXUrl(
																value?.images[0],
																'h=240&w=240&fit=crop',
																80,
																2,
															)}
															loader={myLoader}
															height={200}
															width={240}
															layout="fixed"
															className="room-grid"
														/>
														<Text
															css={{
																fontSize: '$3',
																color: '$grey11',
																fontWeight: '$bold',
																lineHeight: 1.33,
																letterSpacing: '-0.29px',
																'@lg': {
																	fontSize: '$4',
																},
															}}
														>
															{value?.name}
														</Text>
													</Column>
												</Fragment>
											))}
									</Row>
								</Element>
							)}

							{data?.propertyHighlights && (
								<Element name="property-highlights">
									<Title
										title="Additional Property Highlights"
										style={{
											paddingBottom: '10px',
										}}
									/>
									<RoomGrid
										data={data?.propertyHighlights}
										isMobile={isMobile}
										loader={myLoader}
										slug={slug}
										route="highlights"
									/>
								</Element>
							)}

							{data?.amenityDisplayList && (
								<Element name="amenities">
									<Title
										title="Amenities"
										style={{
											paddingBottom: '10px',
										}}
									/>
									<AmenitiesSection
										data={data?.amenityDisplayList?.map(item => ({
											name: item.amenityName,
											icon: <Included />,
										}))}
										isMobile={isMobile}
										showTitle={false}
									/>
								</Element>
							)}

							<Column
								gap={2}
								css={{
									all: 'reset',
									width: 'calc(100% + 32px)',
									marginLeft: '-16px',
									backgroundColor: '#E5F9F3',
									paddingTop: '$6',
									paddingBottom: '$6',
									paddingLeft: '10%',
									paddingRight: '10%',
									'@lg': {
										display: 'none',
									},
								}}
							>
								<Text
									css={{
										padding: '4px 12px 4px 12px',
										border: '1px solid rgba(44,47,66,.4)',
										borderRadius: '$1',
										fontSize: '$3',
										fontWeight: '$bold',
										color: 'rgba(44,47,66,.6)',
										margin: 'auto',
									}}
								>
									FOR YOUR INFO
								</Text>
								<Column
									gap={3}
									css={{
										paddingTop: '$3',
										paddingLeft: '12%',
									}}
								>
									{[
										{
											icon: <TickMark />,
											text: 'The Pickyourtrail Advantage',
										},
										{
											icon: <TickMark />,
											text: 'Flights & hotel prices included',
										},
										{
											icon: <TickMark />,
											text: 'Realtime prices',
										},
										{
											icon: <TickMark />,
											text: 'Instant booking confirmation',
										},
										{
											icon: <TickMark />,
											text: 'Value add-ons',
										},
										{
											icon: <TickMark />,
											text: 'Honeymoon specials',
										},
									].map(({ icon, text }, index) => (
										<Row
											key={'FYI-' + index}
											gap={2}
											css={{
												alignItems: 'center',
											}}
										>
											{icon}
											<Text
												css={{
													fontSize: '17px',
													color: '#2c2f42',
													lineHeight: 1.41,
												}}
											>
												{text}
											</Text>
										</Row>
									))}
								</Column>
							</Column>
							{seoData?.description ? (
								<Element name="overview">
									<SeoDescWrapper
										className="seo-short-desc"
										css={{
											height: `auto`,
										}}
									>
										<SeoTextSection
											className="seo-text"
											dangerouslySetInnerHTML={{
												__html: seoData?.description || '',
											}}
										/>
									</SeoDescWrapper>
								</Element>
							) : (
								<>
									{data?.overview && (
										<Element name="overview">
											<Title title="Overview" />
											<Text
												as="p"
												css={{
													fontSize: '15px',
													lineHeight: 1.2,
													letterSpacing: '-0.3px',
												}}
											>
												{data.overview}
											</Text>
										</Element>
									)}
								</>
							)}
							{pageContents?.map((item, index) => {
								return (
									<Box key={'desitination-components-' + index}>
										{getPageDataComponents({
											item,
											index,
											partnersPage: false,
											FaqDefaultOpen: false,
											resort: true,
										})}
									</Box>
								)
							})}
							{!seoData?.description && data?.seoDetails && (
								<Column
									css={{
										gap: 8,
									}}
								>
									<SEOBlock
										seoH1Title={data?.seoDetails?.h1Title}
										seoContent={data?.seoDetails?.seoContent}
										openState={true}
									/>
								</Column>
							)}
							<BreadCrumb
								useWindow
								homeLink="/"
								textColor={'#777777'}
								iconColor={'#777777'}
								routes={[
									{
										name: 'Maldives Packages',
										currentPage: false,
										link: '/packages/maldives',
									},
									{
										name: `${data?.resortName} Maldives Resort`,
										currentPage: false,
										link: `${router.asPath.split('&')[0]}`,
									},
								]}
								isMobile={isMobile}
							/>
						</Column>
						{/* right side content */}
						<Column
							css={{
								width: '368px',
								// position: 'absolute',
								marginTop: -70,
								zIndex: 9999,
								'@smMax': {
									display: 'none',
								},
							}}
						>
							<Sticky top={0} innerZ={999} flex={1}>
								<Column gap={8}>
									<Row
										css={{
											width: '100%',
											minHeight: '90px',
											padding: '16px 24px',
											alignItems: 'center',
											backgroundColor: '$white',
											zIndex: 99,
											boxShadow: '0 -2px 10pxrgba(0,0,0,.1)',
											border: '1px solid rgba(0,0,0,.1)',
											borderBottomLeftRadius: '4px',
											borderBottomRightRadius: '4px',
										}}
									>
										<Button
											css={{
												width: '100%',
												backgroundColor: '$primary04',
												fontSize: '$3',
												fontWeight: '$bold',
											}}
											onClick={() =>
												showGetCostModal({
													name: data?.resortName,
													resortCode: data?.resortCode,
												})
											}
										>
											Check availability and price
										</Button>
									</Row>
									<Column
										gap={3}
										css={{
											width: '100%',
											padding: '16px 24px 48px',
											backgroundColor: '$white',
											zIndex: 99,
											boxShadow: '0 -2px 10pxrgba(0,0,0,.1)',
											border: '1px solid rgba(0,0,0,.1)',
											borderRadius: '4px',

											'&::before': {
												position: 'absolute',
												top: '27%',
												left: 'calc(96.3% - 50%)',
												content: '',
												width: '$5',
												height: '35px',
												marginLeft: '-10px',
												background:
													'url(https://pyt-images.imgix.net/images/web_app/speed-boat/pyt-flag.svg) no-repeat 50%/cover',
											},
										}}
									>
										<Text
											css={{
												marginTop: '$1',
												width: '100%',
												fontSize: '15px',
												fontWeight: '$bold',
												lineHeight: 1.2,
												letterSpacing: '-0.3px',
												textAlign: 'center',
												color: '#2C2F42',
											}}
										>
											Why book with Pickyourtrail
										</Text>
										<TestimonialCard
											data={shuffle(testimonials || []).slice(0, 5)}
										/>
									</Column>
								</Column>
							</Sticky>
						</Column>
					</Row>
				</ContentWrapper>

				<Row
					css={{
						width: '100%',
						minHeight: '64px',
						padding: '8px 16px',
						alignItems: 'center',
						backgroundColor: '$white',
						zIndex: 99,
						position: 'fixed',
						bottom: 0,
						boxShadow: '0 -2px 10pxrgba(0,0,0,.1)',
						borderTop: '1px solid #E4F1EE',
						'@sm': {
							display: 'none',
						},
					}}
				>
					<Button
						css={{
							width: '100%',
							backgroundColor: '$primary04',
							fontSize: '$3',
							fontWeight: '$bold',
						}}
						onClick={() =>
							showGetCostModal({
								name: data?.resortName,
								resortCode: data?.resortCode,
							})
						}
					>
						Check availability and price
					</Button>
				</Row>

				<GCMContainer oldFlow={isGlobalUser} />
				<Dialog.Root
					open={openRoom}
					onOpenChange={() => {
						router.push(`/${slug}`, null, { shallow: true })
						setOpenRoom(false)
					}}
				>
					<Dialog.Content
						css={{
							all: 'unset',
							position: 'fixed',
							top: 0,
							bottom: 0,
							width: '100%',
							height: '100vh',
							backgroundColor: '$black',
							display: 'flex',
							flexDirection: 'column',
						}}
						closeIcon={
							<Box css={{ cursor: 'pointer' }}>
								<Close
									css={{
										height: '24px',
										width: '24px',
										fill: '$white',
									}}
								/>
							</Box>
						}
					>
						<ContentWrapper>
							<Row
								css={{
									justifyContent: 'space-between',

									'@smMax': {
										flexDirection: 'column',
									},
								}}
							>
								<Text
									css={{
										fontSize: '17px',
										lineHeight: 1.41,
										letterSpacing: '-0.25px',
										color: 'hsla(0,0%,100%,.5)',
										width: '100%',
										'@lg': {
											width: '33.33%',
										},
									}}
								>
									{data?.resortName}
								</Text>
								<Row
									css={{
										gap: '$6',
										'@smMax': {
											marginTop: '$3',
											gap: '$4',
										},
									}}
								>
									{galleryTabs.map((val, index) => (
										<Row key={'gallery-tabs-' + index}>
											<Text
												css={{
													fontSize: '15px',
													lineHeight: 1.41,
													letterSpacing: '-0.25px',
													color:
														selectedGalleryTab === val.route
															? '#fff'
															: 'hsla(0,0%,100%,.5)',
													fontWeight: '$bold',
													cursor: 'pointer',
												}}
												onClick={() => {
													router.push(
														`/${slug}?gallery=${val.route}&index=0`,
														null,

														{
															shallow: true,
															scroll: false,
														},
													)
													setSelectedGalleryTab(val.route)
												}}
											>
												{val.text}
											</Text>
										</Row>
									))}
								</Row>
								<Box
									css={{
										width: '33.33%',
									}}
								></Box>
							</Row>
						</ContentWrapper>
						<Box
							css={{
								height: '70%',
								width: '50%',
								margin: 'auto',

								'@smMax': {
									height: '100%',
									width: '100%',
								},
							}}
						>
							<Text
								css={{
									color: '$white',
									fontWeight: '$bold',
									fontSize: '17px',
									paddingLeft: '$4',
									width: '100%',
									'@lg': {
										display: 'none',
									},
								}}
							>
								{
									data?.[routeMapping?.[selectedGalleryTab]]?.[query.index || 0]
										?.name
								}
							</Text>

							{query.gallery !== 'property' && (
								<Row
									css={{
										marginTop: '$6',
										justifyContent: 'space-between',

										'@lg': {
											marginBottom: '$4',
										},
									}}
								>
									<Row
										gap={1}
										css={{
											cursor: query.index !== '0' ? 'pointer' : 'not-allowed',
											alignItems: 'center',
											'@smMax': {
												paddingLeft: '$3',
											},
										}}
										onClick={() =>
											query.index !== '0'
												? router.push(
														`/${slug}?gallery=
														${query.gallery}&index=${+query.index - 1}`,
														null,
														{
															shallow: true,
															scroll: false,
														},
												  )
												: null
										}
									>
										<ChevronLeft
											width="14px"
											height="14px"
											fill="hsla(0,0%,100%,.66)"
										/>
										<Text
											css={{
												color: 'hsla(0,0%,100%,.66)',
												fontWeight: '$bold',
												fontSize: '15px',
												userSelect: 'none',

												'@smMax': {
													fontSize: '13px',
												},
											}}
										>
											Prev {capitializeURL(query?.gallery)}
										</Text>
									</Row>
									<Text
										css={{
											flex: 1,
											textAlign: 'center',
											color: '$white',
											fontWeight: '$bold',
											fontSize: '17px',

											'@smMax': {
												display: 'none',
											},
										}}
									>
										{
											data?.[routeMapping?.[selectedGalleryTab]]?.[
												query.index || 0
											]?.name
										}
									</Text>
									<Row
										gap={1}
										css={{
											cursor:
												query.index !== `${currentGalleryObj?.length - 1}`
													? 'pointer'
													: 'not-allowed',
											alignItems: 'center',
											'@smMax': {
												paddingRight: '$3',
											},
										}}
										onClick={() =>
											query.index !== `${currentGalleryObj?.length - 1}`
												? router.push(
														`/${slug}?gallery=
														${query.gallery}&index=${+query.index + 1}`,
														null,
														{
															shallow: true,
															scroll: false,
														},
												  )
												: null
										}
									>
										<Text
											css={{
												color: 'hsla(0,0%,100%,.66)',
												fontWeight: '$bold',
												fontSize: '15px',
												userSelect: 'none',

												'@smMax': {
													fontSize: '13px',
												},
											}}
										>
											Next {capitializeURL(query?.gallery)}
										</Text>
										<ChevronRight
											width="14px"
											height="14px"
											fill="hsla(0,0%,100%,.66)"
										/>
									</Row>
								</Row>
							)}

							<ImageSlider
								imageList={
									query.gallery === 'property'
										? data?.resortImages
										: data?.[routeMapping?.[selectedGalleryTab]]?.[
												query.index || 0
										  ]?.images
								}
								alt={query.gallery}
								showThumbs
							/>
						</Box>
					</Dialog.Content>
				</Dialog.Root>
			</Column>
		</>
	)
}

export default MaldivesPrecostedPage
